import {
  collection,
  query,
  addDoc,
  where,
  getDocs,
  deleteDoc,
  doc,
  onSnapshot,
  Unsubscribe,
} from "firebase/firestore";
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { FirebaseContext } from "./FirebaseContext";
import { BusinessContext } from "./BusinessContext";
import { IBusiness } from "../interfaces/IBusiness";
import { IUser } from "../interfaces/IUser";
import { salasConfig } from "../interfaces/constants/salas";

export interface ISalasProps {
  books: ICompleteBook[];
  allBusinessBooks: ICompleteBook[];
  allBooksById: ICompleteBook[];
  allBooks: ICompleteBook[];
  salas: ISala[];
  booksToday: ICompleteBook[];
  addBook: (data: IBook) => Promise<void>;
  getAllBooksById: (id: string) => Promise<Unsubscribe>;
  deleteBook: (id: string) => Promise<void>;
  checkOpenDoor: (sala: string) => Promise<ICompleteBook | undefined>;
  loading: boolean;
  loadingById: boolean;
  loadingBusinessBooks: boolean;
  getBooksById: (id: string, daySelected: number) => Promise<Unsubscribe>;
  checkBooks: (daySelected: number, timeSelected: number, salaId?: string) => Promise<boolean>;
  getAllBooks: () => Promise<Unsubscribe>;               // tiempo real global (devuelve unsubscribe)
  getAllBooksByBusiness: () => Promise<Unsubscribe>; // tiempo real para el negocio del usuario
}

export type IBook = {
  id?: string;
  salaId: string;
  bookedBy: string;
  businessId: string;
  daySelected: number;     // medianoche del día
  timeSelected: number;    // timestamp de la hora exacta
  type?: "web";
};

export type ISala = {
  id?: string;
  name: string;
  type: "boardroom" | "polivalent" | "bmc";
  capacity: string;
  src?: string;
  onlyReception?: boolean;
};

export interface ICompleteBook extends IBook {
  salaData: ISala;
  businessData: IBusiness;
  userData?: IUser;
}

export const SalasContext = createContext<ISalasProps>(null as any);

export const SalasProvider: any = ({ children }: any) => {
  const [books, setBooks] = useState<ICompleteBook[]>([]);
  const [allBooksById, setAllBooksById] = useState<ICompleteBook[]>([]);
  const [allBusinessBooks, setAllBusinessBooks] = useState<ICompleteBook[]>([]);
  const [allBooks, setAllBooks] = useState<ICompleteBook[]>([]);
  const [booksToday, setBooksToday] = useState<ICompleteBook[]>([]);
  const [salas, setSalas] = useState<ISala[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingById, setLoadingById] = useState(true);
  const [loadingBusinessBooks, setLoadingBusinessBooks] = useState(true);

  const { getBusinessById } = useContext(BusinessContext);
  const { db, user, getUserData } = useContext(FirebaseContext);

  const today = new Date().setHours(0, 0, 0, 0);

  // Cache para empresas (evita lecturas repetidas)
  const businessCache = new Map<string, IBusiness>();

  const mapBook = useCallback(async (docSnap: any): Promise<ICompleteBook> => {
    const data = { id: docSnap.id, ...docSnap.data() } as IBook;

    const salaBook =
      salas.find((s) => s.id === data.salaId) ?? {
        id: data.salaId,
        name: "(Sala desconocida)",
        type: "boardroom" as ISala["type"],
        capacity: "0",
      };

    let businessBook = businessCache.get(data.businessId);
    if (!businessBook) {
      try {
        businessBook = await getBusinessById(data.businessId);
      } catch {
        businessBook = {
          id: data.businessId,
          name: "(Empresa desconocida)",
          nameToFilter: "",
          nif: "",
          locals: [],
        } as IBusiness;
      }
      businessCache.set(data.businessId, businessBook);
    }

    const userBook = !data.type ? await getUserData(data.bookedBy) : undefined;

    return { ...data, salaData: salaBook, businessData: businessBook, userData: userBook };
  }, [salas, getBusinessById, getUserData, businessCache]);

  /** Crear reserva */
  const addBook = async (data: IBook) => {
    await addDoc(collection(db, "sala-books"), { ...data });
  };

  /** Verificar disponibilidad */
  const checkBooks = async (daySelected: number, timeSelected: number, salaId: string) => {
    const q = query(
      collection(db, "sala-books"),
      where("salaId", "==", salaId),
      where("daySelected", "==", daySelected),
      where("timeSelected", "==", timeSelected)
    );
    const snap = await getDocs(q);
    return snap.empty;
  };

  /** Eliminar reserva */
  const deleteBook = async (id: string) => {
    await deleteDoc(doc(db, "sala-books", id));
  };

  /** Reservas por sala + día (tiempo real) */
  const getBooksById = async (id: string, daySelected: number) => {
    setLoading(true);
    const q = query(
      collection(db, "sala-books"),
      where("salaId", "==", id),
      where("daySelected", "==", daySelected)
    );

    const unsubscribe = onSnapshot(q, async (snap) => {
      try {
        const mapped = await Promise.all(snap.docs.map(mapBook));
        setBooks(mapped);
      } catch (error) {
        console.error("Error en getBooksById:", error);
        setBooks([]);
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe; // ✅ Devolver unsubscribe para cleanup
  };

  /** Todas las reservas por sala (tiempo real) */
  const getAllBooksById = async (id: string) => {
    setLoadingById(true);
    const q = query(collection(db, "sala-books"), where("salaId", "==", id));

    const unsubscribe = onSnapshot(q, async (snap) => {
      try {
        const mapped = await Promise.all(snap.docs.map(mapBook));
        setAllBooksById(mapped);
      } catch (error) {
        console.error("Error en getAllBooksById:", error);
        setAllBooksById([]);
      } finally {
        setLoadingById(false);
      }
    });

    return unsubscribe; // ✅ Devolver unsubscribe para cleanup
  };

  /** Todas las reservas de un negocio del usuario (tiempo real) — SIN orderBy para evitar índice */
  const getAllBooksByBusiness = useCallback(async (): Promise<Unsubscribe> => {
    if (!user?.businessId) {
      setLoadingBusinessBooks(false);
      return () => {};
    }

    setLoadingBusinessBooks(true);
    const q = query(
      collection(db, "sala-books"),
      where("daySelected", ">=", today),
      where("businessId", "==", user.businessId)
    );

    const unsub = onSnapshot(q, async (snap) => {
      try {
        const mapped = await Promise.all(snap.docs.map(mapBook));
        // Se ordena en cliente como antes
        setAllBusinessBooks(mapped.sort((a, b) => a.timeSelected - b.timeSelected));
      } catch (error) {
        console.error("Error en getAllBooksByBusiness:", error);
        setAllBusinessBooks([]);
      } finally {
        setLoadingBusinessBooks(false);
      }
    });

    return unsub;
  }, [user?.businessId, db, today, mapBook]);

  /** Todas las reservas del sistema (tiempo real) — SIN orderBy, ordena en cliente */
  const getAllBooks = async () => {
    const q = query(collection(db, "sala-books"), where("daySelected", ">=", today));

    const unsubscribe = onSnapshot(q, async (snap) => {
      try {
        const mapped = await Promise.all(snap.docs.map(mapBook));
        const ordered = mapped.sort((a, b) => a.daySelected - b.daySelected || a.timeSelected - b.timeSelected);
        setAllBooks(ordered);
        filterBooksToday(ordered);
      } catch (error) {
        console.error("Error en getAllBooks:", error);
        setAllBooks([]);
        setBooksToday([]);
      }
    });

    return unsubscribe; // ✅ Devolver unsubscribe para cleanup
  };

  /** Filtrar reservas de hoy */
  const filterBooksToday = (booksIn: ICompleteBook[]) => {
    const todayNum = new Date().setHours(0, 0, 0, 0);
    setBooksToday(booksIn.filter((b) => b.daySelected === todayNum));
  };

  /** Cargar catálogo de salas */
  const getSalas = useCallback(async () => {
    const q = query(collection(db, "salas"));
    const snap = await getDocs(q);

    const arr: ISala[] = snap.docs.map((d) => {
      const raw = d.data() as Partial<ISala>;
      const allowed: ISala["type"][] = ["boardroom", "polivalent", "bmc"];
      const type = allowed.includes(raw.type as any) ? (raw.type as ISala["type"]) : "boardroom";
      return {
        id: d.id,
        name: raw.name ?? "(sin nombre)",
        type,
        capacity: raw.capacity ?? "0",
        src: raw.src ?? undefined,
        onlyReception: raw.onlyReception ?? false,
      };
    });

    setSalas(arr);
  }, [db]);

  /** Verificar si puede abrirse la puerta en este momento */
  const checkOpenDoor = async (sala: string) => {
    const idSalaToCheck = salasConfig[sala.toLowerCase()];
    const dateNow = new Date().setMinutes(0, 0, 0);
    if (idSalaToCheck && user?.businessId) {
      return booksToday.find(
        (b) =>
          b.salaId === idSalaToCheck &&
          new Date(b.timeSelected).getHours() === new Date(dateNow).getHours() &&
          b.businessId === user.businessId
      );
    }
  };

  // Carga de salas al montar - Solo si hay usuario autenticado
  useEffect(() => {
    if (!user) return; // ✅ No ejecutar si no hay usuario
    
    const loadSalas = async () => {
      try {
        await getSalas();
      } catch (error) {
        console.error("Error cargando salas:", error);
      }
    };
    loadSalas();
  }, [db, user]); // ✅ Depende de db y user

  // Suscripción a reservas del negocio del usuario y globales.
  // Solo ejecuta si hay usuario autenticado Y las salas ya están cargadas
  useEffect(() => {
    if (!user) return; // ✅ No ejecutar si no hay usuario
    if (salas.length === 0) return; // ✅ Esperar a que las salas estén cargadas

    let unsubBusiness: Unsubscribe | undefined;
    let unsubGlobal: Unsubscribe | undefined;

    (async () => {
      try {
        if (user.businessId) {
          unsubBusiness = await getAllBooksByBusiness();
        } else {
          setLoadingBusinessBooks(false);
        }
        // Global (opcional si lo usas en otras vistas)
        unsubGlobal = await getAllBooks();
      } catch (error) {
        console.error("Error configurando suscripciones:", error);
        setLoadingBusinessBooks(false);
      }
    })();

    return () => {
      if (unsubBusiness) unsubBusiness();
      if (unsubGlobal) unsubGlobal();
    };
  }, [user?.businessId, user, salas.length]); // ✅ Ahora espera a que salas esté cargado

  return (
    <SalasContext.Provider
      value={{
        books,
        allBooks,
        allBooksById,
        allBusinessBooks,
        booksToday,
        checkOpenDoor,
        getBooksById,
        getAllBooksById,
        salas,
        addBook,
        loading,
        loadingById,
        loadingBusinessBooks,
        deleteBook,
        checkBooks,
        getAllBooks,
        getAllBooksByBusiness,
      }}
    >
      {children}
    </SalasContext.Provider>
  );
};
